import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '../../../api/apiClient';
import ErrorBanner from '../../../components/ui/ErrorBanner';
import { st, colors } from '../shared/meetingStyles';
import Avatar from '../shared/Avatar';
import { UsersIcon, XIcon } from '../shared/Icons';

export default function ParticipantsPanel({ meetingId, onClose }) {
  const [participants, setParticipants] = useState([]);
  const [team, setTeam] = useState([]);
  const [error, setError] = useState(null);
  const [addUserId, setAddUserId] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const [pRes, tRes] = await Promise.all([
        apiClient.get(`/meetings/${meetingId}/participants`),
        apiClient.get('/meetings/team'),
      ]);
      setParticipants(pRes.data.participants || []);
      setTeam(tRes.data.users || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, [meetingId]);

  useEffect(() => { load(); }, [load]);

  const nonParticipants = useMemo(() => {
    const inMeeting = new Set(participants.map(p => p.id));
    return team.filter(u => !inMeeting.has(u.id));
  }, [participants, team]);

  const addParticipant = async (e) => {
    e.preventDefault();
    if (!addUserId) return;
    await apiClient.post(`/meetings/${meetingId}/participants`, { user_id: addUserId });
    setAddUserId('');
    await load();
  };

  const removeParticipant = async (userId) => {
    await apiClient.delete(`/meetings/${meetingId}/participants/${userId}`);
    await load();
  };

  return (
    <div style={{ ...st.card, marginBottom: 0 }}>
      {error && <ErrorBanner message={error} />}
      <div style={st.cardHeader}>
        <h3 style={{ ...st.h3, display: 'flex', alignItems: 'center', gap: '0.4rem' }}><UsersIcon size={16} /> Meeting Participants</h3>
        <button onClick={onClose} style={st.ghostBtn}><XIcon size={13} /></button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.9rem' }}>
        {participants.length === 0 && <span style={st.subtext}>No participants yet.</span>}
        {participants.map(p => (
          <span key={p.id} style={{
            display: 'flex', alignItems: 'center', gap: '0.4rem', background: colors.primarySoft, color: colors.primaryDark,
            borderRadius: '999px', padding: '0.3rem 0.5rem 0.3rem 0.35rem', fontSize: '0.78rem', fontWeight: '700',
          }}>
            <Avatar name={p.name} size={20} />
            {p.name}
            <button onClick={() => removeParticipant(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.primaryDark, padding: 0, opacity: 0.6, display: 'inline-flex' }} title="Remove"><XIcon size={11} /></button>
          </span>
        ))}
      </div>
      {nonParticipants.length > 0 && (
        <form onSubmit={addParticipant} style={{ display: 'flex', gap: '0.5rem' }}>
          <select value={addUserId} onChange={e => setAddUserId(e.target.value)} style={{ ...st.input, width: '220px' }}>
            <option value="">Add a person...</option>
            {nonParticipants.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <button type="submit" disabled={!addUserId} style={st.primaryBtn}>Add</button>
        </form>
      )}
    </div>
  );
}
